import find from 'lodash/find'
import some from 'lodash/some'
import last from 'lodash/last'
import isEmpty from 'lodash/isEmpty'
import first from 'lodash/first'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'
import { Material as MaterialInterface, MaterialWithUnits, Unit } from '../../interfaces/Material'
import { LP_UNIT_TYPE, MATERIAL_OPTIONS_PREFIXES } from '@utils/constants/learningPath'

const isAllNotDeleted = (materials: MaterialInterface[]) =>
  some(materials, ({ Status }) => Status === 'New' || Status === 'Active' || Status === 'Complete')

export const getMaterial = (Id: string, MaterialOptions: MaterialWithUnits[]) => {
  return find(MaterialOptions, { Id })
}

export const getUnit = (Id: string, Units: Unit[]) => {
  return find(Units, (unit) => `${unit.Id}` === Id)
}

export const getUnits = (Units: Unit[] = []) =>
  Units.filter(
    ({ Name = '', UnitType }) =>
      !MATERIAL_OPTIONS_PREFIXES.some((prefix) => Name.toLowerCase().includes(prefix.toLowerCase())) &&
      UnitType !== LP_UNIT_TYPE.CULTURE
  )

const determineUnit = (materialOptions: MaterialWithUnits[], prev: MaterialInterface, isFlex: boolean) => {
  const mOption = find(materialOptions, { Id: prev?.Material })
  let units = get(mOption, 'Units', materialOptions?.[0]?.Units ?? [])
  units = isFlex ? units : getUnits(units)
  const index = findIndex(units, (val) => `${val.Id}` === prev?.Chapter)
  let unit
  if (index > -1) {
    unit = units[index + 1]
    if (!unit) {
      unit = units[index]
    }
  } else {
    unit = units[0]
  }
  return unit
}

/**
 * By default, the material and unit/chapter displayed will be a suggestion and this will be based on previous lesson registered + 1.
 * For instance: if the material from the previous lesson was Unit 2, then Unit 3 will be pre-selected.
 *
 * If the instructor opens the lesson registration, saves the record and go back to my schedule,
 * Materials and Unit/chapter selected in the lesson registration will be displayed.
 * reference: https://berlitz.atlassian.net/browse/PTL-433
 **/

const getSuggestedMaterial = (
  prevMaterials: MaterialInterface[],
  materials: MaterialInterface[],
  materialOptions: MaterialWithUnits[],
  isFlex?: boolean,
  activeMaterial?: string,
  stateChapter?: string
): MaterialInterface => {
  if (activeMaterial) {
    /**
     * If the selected material is the same with the previious material
     * then we will use the previous material's chapter
     */
    if (last(prevMaterials)?.Material === activeMaterial) {
      const prev = last(prevMaterials)!
      if (!stateChapter) {
        const unit = determineUnit(materialOptions, prev, !!isFlex)
        return {
          ...last(prevMaterials)!,
          Chapter: `${get(unit, 'Id', prev.Chapter)}`,
        }
      } else {
        return {
          ...last(prevMaterials)!,
          Chapter: stateChapter,
        }
      }
    }

    /**
     * Lets get the chapter 0 from material options
     */
    const matOpt = find(materialOptions, { Id: activeMaterial })

    return {
      Material: activeMaterial,
      Chapter: `${matOpt?.Units?.[0]?.Id}` ?? '',
      Page: '',
      Point: '',
      Status: '',
      URL: '',
    }
  }

  if (materials.length > 0 && isAllNotDeleted(materials)) {
    return last(materials)!
  }

  let prev = (last(prevMaterials) || {}) as MaterialInterface
  if (isEmpty(prev) && materialOptions.length) {
    const material = first(materialOptions)
    prev = {
      Material: material?.Id ?? '',
      Chapter: material?.Chapter ?? '',
      Page: material?.Page ?? '',
      Point: material?.Point ?? '',
      Status: '',
      URL: '',
    }
  }

  const unit = determineUnit(materialOptions, prev, !!isFlex)

  return {
    ...prev,
    Chapter: `${get(unit, 'Id', prev.Chapter)}`,
    ChapterName: get(unit, 'Name', prev.Chapter),
  }
}

export default getSuggestedMaterial
